import {
  addDays,
  differenceInCalendarDays,
  format,
  parse,
  isValid
} from 'date-fns'
import { is, isEmpty } from 'ramda'
import config from 'isomorphic-config'
import {
  PrePopQueryHotels,
  FlightsQuery,
  PrePopHotels
} from './typeahead-fetch-types'
import {
  apiRequest,
  retailSearchApiRequest,
  flightsLocationRequest
} from './typeahead-fetch'
import {
  SetValueFunctionType,
  FlightFormStateType,
  FlightRecordType
} from '../components/Flights/types'
import { QueryString } from '../types'

const stringToArray = (value: string | string[]) =>
  typeof value === 'string' ? [value] : value

const makeUrls = (values: string[]) =>
  values.map(
    (value: string) =>
      `${config.client.typeahead.location.flights}${value}${config.client.typeahead.location.suffix.flights}`
  )

const adjustLengthOfStay = (checkInDate: Date, checkOutDate: Date) => {
  const lengthOfStay = differenceInCalendarDays(checkOutDate, checkInDate)
  let adjustedCheckOutDate
  if (lengthOfStay < 1) {
    adjustedCheckOutDate = addDays(checkInDate, 1)
  } else if (lengthOfStay > 28) {
    adjustedCheckOutDate = addDays(checkInDate, 28)
  } else {
    adjustedCheckOutDate = checkOutDate
  }
  return { checkInDate, checkOutDate: adjustedCheckOutDate }
}

const parseDate = (date: string) => {
  if (date.toLowerCase() === 'today') {
    return new Date()
  }
  if (date.toLowerCase() === 'tomorrow') {
    return addDays(new Date(), 1)
  }
  if (date.match(/^\d{8}$/)) {
    return parse(date, 'yyyyMMdd', new Date())
  }
  return new Date(date.replace(/-/g, '/'))
}

const getCheckInOutDates = (checkIn: string, checkOut: string) => {
  const parsedCheckIn = parseDate(checkIn)
  const parsedCheckOut = parseDate(checkOut)
  if (!isValid(parsedCheckIn) || !isValid(parsedCheckOut)) return {}
  const { checkInDate, checkOutDate } = adjustLengthOfStay(
    parsedCheckIn,
    parsedCheckOut
  )
  const startDate = format(checkInDate, 'yyyyMMdd')
  const endDate = format(checkOutDate, 'yyyyMMdd')
  return { startDate, endDate }
}

const getRooms = (rooms: string) => {
  let parsedRooms = parseInt(rooms, 10)
  if (!parsedRooms || !is(Number, parsedRooms) || parsedRooms < 1) {
    parsedRooms = 1
  } else if (parsedRooms > 9) {
    parsedRooms = 9
  }
  return parsedRooms
}

const getPrioritzedLocation = async ({
  airportCode,
  cityId,
  cityName,
  poiId
}: {
  airportCode?: string
  cityId?: string
  cityName?: string
  poiId?: string
}) => {
  let locationObject = null
  if (poiId) {
    locationObject = await retailSearchApiRequest({ poiId })
  } else if (airportCode) {
    locationObject = await apiRequest({ airportCode })
  } else if (cityId) {
    locationObject = await retailSearchApiRequest({ cityId })
  } else if (cityName) {
    locationObject = await apiRequest({ cityName })
  }
  return locationObject
}

const hotelsQueryParams = [
  'airport-code',
  'city-id',
  'city-name',
  'poi-id',
  'check-in',
  'check-out',
  'rooms'
]

export function isValidHotelsPrePopQuery(
  query: QueryString
): query is PrePopQueryHotels {
  return Object.keys(query).some(key => hotelsQueryParams.includes(key))
}

export default async function prePopulateHotelValues(
  query: QueryString
): Promise<Partial<PrePopHotels> | null> {
  if (isValidHotelsPrePopQuery(query)) {
    const {
      'airport-code': airportCode,
      'check-in': checkIn,
      'check-out': checkOut,
      'city-id': cityId,
      'city-name': cityName,
      'poi-id': poiId,
      rooms
    } = query as Partial<PrePopQueryHotels>
    const prePopulatedValues: Partial<PrePopHotels> = {}
    if (airportCode || cityId || cityName || poiId) {
      const locationObject = await getPrioritzedLocation({
        airportCode,
        cityId,
        cityName,
        poiId
      })
      if (locationObject && !isEmpty(locationObject)) {
        prePopulatedValues.endLocation = locationObject
      }
    }

    if (checkIn && checkOut) {
      const { startDate, endDate } = getCheckInOutDates(checkIn, checkOut)
      prePopulatedValues.startDate = startDate
      prePopulatedValues.endDate = endDate
    }

    if (rooms) {
      const roomCount = getRooms(rooms)
      prePopulatedValues.roomCount = roomCount
    }
    return prePopulatedValues
  }
  return null
}

function customZipWith({
  originsLocationItem,
  destinationsLocationItem,
  startDate: startDates
}: {
  originsLocationItem: Location[]
  destinationsLocationItem: Location[]
  startDate: string[]
}) {
  const len = Math.max(
    originsLocationItem.length,
    destinationsLocationItem.length,
    startDates.length
  )

  return new Array(len).fill({}).map((_, index) => {
    return {
      startLocation: originsLocationItem[index]
        ? originsLocationItem[index]
        : {},
      endLocation: destinationsLocationItem[index]
        ? destinationsLocationItem[index]
        : {},
      startDate: startDates[index] ? startDates[index] : ''
    }
  })
}

const cabinClasses = {
  economy: 'Economy',
  'premium economy': 'Premium Economy',
  business: 'Business',
  first: 'First'
} as const

const flightsQueryParams = [
  'origin',
  'destination',
  'startDate',
  'endDate',
  'cabinClass',
  'adultCount',
  'youthCount',
  'infantCount',
  'childrenCount'
]

function isValidFlightsQuery(query: FlightsQuery) {
  return Object.keys(query).some(key => flightsQueryParams.includes(key))
}

export async function prepopulateFlightsValues(
  query: FlightsQuery,
  setValue: SetValueFunctionType<FlightFormStateType>
) {
  if (!isValidFlightsQuery(query)) {
    return
  }

  let { origin = '', destination = '', startDate = '', endDate = '' } = query

  const {
    cabinClass = 'economy',
    adultCount = '1',
    youthCount = '0',
    infantCount = '0',
    childrenCount = '0'
  } = query

  origin = stringToArray(origin)
  destination = stringToArray(destination)
  startDate = stringToArray(startDate)
  endDate = stringToArray(endDate)
  const originUrls = makeUrls(origin)
  const destinationUrls = makeUrls(destination)

  const originsLocationItem = await flightsLocationRequest(originUrls)

  const destinationsLocationItem = await flightsLocationRequest(destinationUrls)
  const flights = customZipWith({
    originsLocationItem: originsLocationItem.filter(item => item !== null),
    destinationsLocationItem: destinationsLocationItem.filter(
      item => item !== null
    ),
    startDate
  }) as FlightRecordType[]

  if (flights.length) {
    if (endDate[0].length > 0) {
      flights[0] = {
        ...flights[0],
        endDate: startDate[0] === endDate[0] ? '' : endDate[0]
      }
    }
    setValue('flights', flights)
    let flightType = 'round-trip'
    if (
      flights.length === 1 &&
      (!isEmpty(flights[0].startLocation) ||
        !isEmpty(flights[0].endLocation)) &&
      startDate[0].length !== 0
    ) {
      flightType = 'one-way'
      if (endDate[0].length !== 0) {
        flightType = 'round-trip'
      }
    } else if (flights.length > 1) {
      flightType = 'multi-destination'
    }
    setValue('flightType', flightType)
  }

  if (cabinClass in cabinClasses) {
    setValue('cabinClass', cabinClasses[cabinClass])
  }

  setValue('adultCount', Number(adultCount))
  setValue('youthCount', Number(youthCount))
  setValue('childrenCount', Number(childrenCount))
  setValue('infantCount', Number(infantCount))
}
